import { useContext, useEffect } from 'react';
import PT from 'prop-types';
import { Chip, Skeleton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { uniq, isEmpty } from 'lodash';
import { useTheme, lighten, darken } from '@mui/material/styles';
import { Box } from '@mui/system';
import { FormSpy } from 'react-final-form';
import Alert from 'components/alerts';
import FinalForm from 'components/forms';
import { Radio, Select, TextField } from 'components/pickers/final-form-fields';
import { ThemeContext } from 'context/theme-context';
import useAddNewAsset from './hooks/use-new-asset';

const AddNewAsset = ({
  setOpenNewAsset,
  node,
  isNewAssetOpen,
  projectLevel,
  setExpanded,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const { localTheme } = useContext(ThemeContext);
  const theme = useTheme();
  const {
    postNewAsset,
    openSnackBar,
    assetTypeOptions,
    typesLoading,
    assetTypeValue,
    setAssetTypeValue,
    assetSubTypesLoading,
    assetSubTypeOptions,
    error,
    names,
    setNames,
    validateAddAssetForm,
  } = useAddNewAsset(
    setOpenNewAsset,
    node,
    isNewAssetOpen,
    projectLevel,
    setExpanded
  );

  useEffect(() => {
    if (openSnackBar.open) {
      const title =
        openSnackBar.variant === 'success'
          ? 'Asset added successfully'
          : `Error: ${openSnackBar.message}`;
      enqueueSnackbar(title, {
        variant: openSnackBar.variant,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSnackBar]);

  const handleChange = (e, values, form) => {
    if (values.assetName && values.assetName.trim() !== '') {
      setNames(uniq([...names, values.assetName]));
      form.mutators.setFormAttribute(
        'assetNames',
        uniq([...names, values.assetName])
      );
      form.mutators.remove('assetName', '');
    }
  };

  const handleKey = (e, values, form) => {
    if (
      e.key === 'Tab' &&
      values.assetName &&
      values.assetName.trim() !== '' &&
      !names.includes(values.assetName)
    ) {
      e.preventDefault();
      e.stopPropagation();
      handleChange(e, values, form);
    }
    if (
      e.key === 'Enter' &&
      values.assetName &&
      values.assetName.trim() !== '' &&
      !names.includes(values.assetName)
    ) {
      e.preventDefault();
      e.stopPropagation();
      handleChange(e, values, form);
      form.submit();
    }
  };

  const handlePaste = (e, form) => {
    e.preventDefault();
    let paste = (e.clipboardData || window.clipboardData).getData('text');
    const pasted = paste.split(/[\t,\r\n]+/).map(value => value.trim());

    const uniqueValues = pasted.filter(
      value => value && !names.includes(value)
    );
    if (uniqueValues.length > 0) {
      setNames(curr => uniq([...curr, ...uniqueValues]));
      form.mutators.setFormAttribute(
        'assetNames',
        uniq([...names, ...uniqueValues])
      );
    }
  };

  const handleNameDelete = (item, form) => {
    setNames(names.filter(v => v !== item));
    form.mutators.setFormAttribute(
      'assetNames',
      names.filter(v => v !== item)
    );
  };

  const handleDelete = (e, form) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleNameDelete(e.target.innerText, form);
    }
  };

  const formFields = [
    {
      field: (
        <FormSpy subscription={{ values: true, mutators: true, errors: true }}>
          {({ form, errors }) => {
            return (
              <Radio
                radioGroupProps={{
                  value: { assetTypeValue },
                  onChange: event => {
                    setAssetTypeValue(event.target.value);
                    form.mutators.setFormAttribute('assetSubType', null);
                  },
                }}
                error={!!errors.assetType}
                label="Select Asset Type"
                options={assetTypeOptions}
                name="assetType"
              />
            );
          }}
        </FormSpy>
      ),
    },
    {
      field: (
        <FormSpy subscription={{ values: true, mutators: true, errors: true }}>
          {({ form, errors }) => {
            // eslint-disable-next-line no-nested-ternary
            return assetSubTypesLoading ? (
              <Skeleton title="subTypeLoading" />
            ) : !assetSubTypesLoading && assetSubTypeOptions.length > 0 ? (
              <Select
                required={true}
                error={!!errors?.assetSubType}
                onChange={(e, val) => {
                  form.mutators.setFormAttribute(
                    'assetSubType',
                    val.props.value
                  );
                  if (isEmpty(names)) {
                    setNames([val.props.children]);
                    form.mutators.setFormAttribute('assetNames', [
                      val.props.children,
                    ]);
                  }
                }}
                label="Asset Sub Type"
                name="assetSubType"
                options={assetSubTypeOptions}
                getOptionValue={option => option.name}
              />
            ) : null;
          }}
        </FormSpy>
      ),
    },
    {
      field: (
        <FormSpy subscription={{ values: true, mutators: true, errors: true }}>
          {({ values, form, errors }) => {
            return assetSubTypesLoading ? (
              <Skeleton title="assetNameLoading" />
            ) : (
              <TextField
                name="assetName"
                label="Asset Name"
                required
                error={!!errors?.assetName}
                onPaste={e => handlePaste(e, form)}
                onKeyDown={e => handleKey(e, values, form)}
                onBlur={e => handleChange(e, values, form)}
                helperText={
                  errors?.assetName
                    ? errors?.assetName
                    : 'hit "Tab" for multiple assets or paste list from excel'
                }
                fullWidth
                InputProps={{
                  startAdornment:
                    names.length === 0 ? null : (
                      <div>
                        {names.map(name => {
                          return (
                            <Chip
                              key={name}
                              aria-label="newAssetNameChip"
                              label={name}
                              onKeyDown={e => handleDelete(e, form)}
                              variant="outlined"
                              sx={{
                                margin: theme => theme.spacing(0.25),
                                backgroundColor:
                                  localTheme === 'dark'
                                    ? darken(theme.palette.primary.main, 0.3)
                                    : lighten(theme.palette.primary.main, 0.8),
                              }}
                              onDelete={() => {
                                handleNameDelete(name, form);
                              }}
                            />
                          );
                        })}
                      </div>
                    ),
                }}
              />
            );
          }}
        </FormSpy>
      ),
    },
  ];

  return (
    <>
      {error && (
        <Box
          sx={theme => ({
            padding: theme.spacing(3),
          })}
        >
          <Alert title={error?.msg} text={error?.details} />
        </Box>
      )}
      <FinalForm
        additionalMutators={{
          setFormAttribute: ([fieldName, fieldVal], state, { changeValue }) => {
            changeValue(state, fieldName, () => fieldVal);
          },
        }}
        aria-label="add-asset"
        title={`Add Asset to ${node.name || 'Project'}`}
        isLoading={typesLoading}
        isPanel={true}
        initialValues={{}}
        resetButton={false}
        validate={validateAddAssetForm}
        cancelButton={true}
        submitButtonLabel="Add Asset"
        formFields={formFields}
        onSubmit={postNewAsset}
        onClose={() => {
          setOpenNewAsset(false);
        }}
      />
    </>
  );
};

AddNewAsset.propTypes = {
  setOpenNewAsset: PT.func.isRequired,
  node: PT.shape({ name: PT.string }).isRequired,
  isNewAssetOpen: PT.bool.isRequired,
  projectLevel: PT.bool.isRequired,
  setExpanded: PT.func.isRequired,
};

export default AddNewAsset;
